import { createRouter as createTanStackRouter } from '@tanstack/react-router';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { FontFamily, TypographyProvider } from '@lili/lili-frontend-common';
import { routeTree } from './routeTree.gen';
import type { ReactNode } from 'react';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { ErrorModal } from '@/components/ErrorModal/ErrorModal';
import { ErrorFallback } from '@/components/ErrorModal/ErrorFallback';
import { errorModalState } from '@/components/ErrorModal/errorModalState';
import { AccountantProvider } from '@/contexts/AccountantProvider.tsx';

interface WrapProps {
  children: ReactNode;
}

export const getRouter = () => {
  const queryCache = new QueryCache({
    onError: (error) => {
      errorModalState.show(error.message);
    },
  });

  const mutationCache = new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (!mutation.options.onError && !mutation.meta?.handlesError) {
        errorModalState.show(error.message);
      }
    },
  });

  const queryClient = new QueryClient({
    queryCache,
    mutationCache,
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: 1000 * 60 * 5,
        retry: false,
      },
    },
  });

  return createTanStackRouter({
    routeTree,
    basepath: '/accountant',
    scrollRestoration: true,
    defaultPreload: 'intent',
    defaultPreloadStaleTime: 0,
    defaultErrorComponent: ErrorFallback,
    context: {
      queryClient,
    },
    Wrap: ({ children }: WrapProps) => {
      return (
        <ErrorBoundary>
          <AccountantProvider>
            <TypographyProvider defaultFontFamily={FontFamily.INTER}>
              <QueryClientProvider client={queryClient}>
                {children}
                <ErrorModal />
                <ReactQueryDevtools initialIsOpen={false} />
              </QueryClientProvider>
            </TypographyProvider>
          </AccountantProvider>
        </ErrorBoundary>
      );
    },
  });
};

declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
